// Channel-action handlers + memoized ChannelActions object, extracted from
// Sidebar.tsx during the P5 split. Owns the four mutation handlers
// (leaveWorkspace, deleteChannel, leaveChannel, toggleArchive)
// plus the memoized `channelActions` ChannelItem consumes.
//
// `channelActionsCtxRef` is load-bearing: every ChannelItem is wrapped in
// React.memo, and Sidebar re-renders frequently (Zero status flips). If the
// `actions` prop's identity changed each render, every ChannelItem would
// re-render. Funnel the live state through a ref so the actions object is
// referentially stable forever — ChannelItem.memo bails out cleanly.

import { useMemo, useRef } from "react";
import { toast } from "sonner";
import { useZero } from "@rocicorp/zero/react";
import { mutators } from "@/lib/mutators";
import { deleteChannelRequest, DeleteApiError } from "@ano/shared/api/delete";
import { API_URL } from "@/lib/config";
import { getAccessTokenWithRetry } from "@/lib/auth-token";
import { postSystemLog } from "@/lib/system-log";
import { useCall } from "@/components/calls/CallProvider";
import type { ChannelActions } from "../ChannelItem";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WorkspaceLike = any;

interface BaseChannel {
  id: string;
  name: string;
  is_archived?: boolean | null;
}

interface MembershipLike {
  id: string;
  channel_id: string;
  muted?: boolean | null;
}

interface ChannelLike extends BaseChannel {
  // ChannelItem dispatches through `BaseChannel`-shaped values for most
  // actions, but the context-set callbacks (edit/delete) preserve full row.
  [key: string]: unknown;
}

interface UseChannelActionsInput {
  user: { id: string } | null | undefined;
  zero: ReturnType<typeof useZero>;
  selectedWorkspace: string | null;
  currentWorkspace: WorkspaceLike;
  myMembers: MembershipLike[];
  channelList: BaseChannel[];
  selectedChannel: string | null;
  onSelectChannel: (id: string | null, name?: string) => void;
  currentUserWsMembership: { id: string; role: string } | undefined;
  channelById: Map<string, BaseChannel>;
  // Dialog setters from useSidebarUIModalStates
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setContextChannel: (ch: any) => void;
  setShowEditChannel: (open: boolean) => void;
  setShowDeleteChannel: (open: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setInviteContext: (ctx: any) => void;
  contextChannel: { id: string; name: string } | null;
  onSelectWorkspace: (id: string | null) => void;
  // Matches the WorkOS `useAuth()` return shape so the parent can pass it
  // through verbatim. `getAccessTokenWithRetry` consumes a narrower type;
  // we adapt at the call site.
  getAccessToken: () => Promise<string | undefined> | string | undefined;
}

export interface UseChannelActionsResult {
  channelActions: ChannelActions;
  handleLeaveWorkspace: () => void;
  handleDeleteChannel: () => Promise<void>;
  handleLeaveChannel: (ch: BaseChannel) => Promise<void>;
  handleToggleArchive: (ch: ChannelLike) => void;
}

export function useChannelActions({
  user,
  zero,
  selectedWorkspace,
  currentWorkspace,
  myMembers,
  channelList,
  selectedChannel,
  onSelectChannel,
  currentUserWsMembership,
  channelById,
  setContextChannel,
  setShowEditChannel,
  setShowDeleteChannel,
  setInviteContext,
  contextChannel,
  onSelectWorkspace,
  getAccessToken,
}: UseChannelActionsInput): UseChannelActionsResult {
  // App-root call provider — used by the huddle action to fire a
  // call into the targeted channel without first navigating into it
  // (Slack Huddles parity).
  const { joinCall } = useCall();
  const handleLeaveWorkspace = () => {
    if (!user || !selectedWorkspace) return;
    const membership = currentUserWsMembership;
    if (membership) {
      for (const m of myMembers) {
        const ch = channelById.get(m.channel_id);
        if (ch) {
          zero.mutate(mutators.channel_members.delete({ id: m.id }));
        }
      }
      zero.mutate(mutators.workspace_members.delete({ id: membership.id }));
    }
    onSelectChannel(null);
    onSelectWorkspace(null);
  };

  const handleDeleteChannel = async () => {
    if (!contextChannel) return;
    if (!selectedWorkspace) {
      toast.error("No selected workspace");
      throw new Error("Workspace missing");
    }

    const token = await getAccessTokenWithRetry(
      async () => (await getAccessToken()) ?? null,
      {
        maxWaitMs: 5000,
        intervalMs: 200,
      },
    );
    if (!token) {
      toast.error("Failed to authenticate deletion request");
      throw new Error("No access token");
    }

    try {
      await deleteChannelRequest({
        apiBaseUrl: API_URL,
        accessToken: token,
        channelId: contextChannel.id,
        workspaceId: selectedWorkspace,
      });
    } catch (error) {
      const message =
        error instanceof DeleteApiError
          ? error.message
          : "Failed to delete channel";
      toast.error(message);
      throw new Error(message);
    }

    postSystemLog(`Channel #${contextChannel.name} was deleted`);
    if (selectedChannel === contextChannel.id) {
      const remaining = channelList.filter((ch) => ch.id !== contextChannel.id);
      onSelectChannel(remaining[0]?.id ?? null);
    }
  };

  const handleLeaveChannel = async (ch: BaseChannel) => {
    if (!user) return;
    const membership = myMembers.find((m) => m.channel_id === ch.id);
    if (membership) {
      zero.mutate(mutators.channel_members.delete({ id: membership.id }));
    }
    if (selectedChannel === ch.id) {
      const remaining = channelList.filter((c) => c.id !== ch.id);
      onSelectChannel(remaining[0]?.id ?? null);
    }
  };

  const handleToggleArchive = (ch: ChannelLike) => {
    zero.mutate(
      mutators.channels.update({
        id: ch.id,
        is_archived: !ch.is_archived,
      }),
    );
    if (ch.is_archived === false && selectedChannel === ch.id) {
      const remaining = channelList.filter((c) => c.id !== ch.id);
      onSelectChannel(remaining[0]?.id ?? null);
    }
  };

  // ─── Stable ChannelItem actions ─────────────────────────────────
  // Sidebar re-renders frequently (multiple Zero selectors flipping status +
  // data). Previously each of the 9 ChannelItem callbacks was recreated per
  // render, forcing every ChannelItem to re-render. Funnel the live state
  // through a ref so the actions object stays referentially stable forever
  // — ChannelItem's React.memo then bails out cleanly.
  const channelActionsCtxRef = useRef({
    onSelectChannel,
    selectedWorkspace,
    currentWorkspace,
    myMembers,
    zero,
    setContextChannel,
    setShowEditChannel,
    setShowDeleteChannel,
    setInviteContext,
    handleToggleArchive,
    handleLeaveChannel,
    joinCall,
  });
  // eslint-disable-next-line react-hooks/refs
  channelActionsCtxRef.current = {
    onSelectChannel,
    selectedWorkspace,
    currentWorkspace,
    myMembers,
    zero,
    setContextChannel,
    setShowEditChannel,
    setShowDeleteChannel,
    setInviteContext,
    handleToggleArchive,
    handleLeaveChannel,
    joinCall,
  };

  const channelActions = useMemo<ChannelActions>(
    () => ({
      select: (c) => channelActionsCtxRef.current.onSelectChannel(c.id, c.name),
      edit: (c) => {
        const ctx = channelActionsCtxRef.current;
        ctx.setContextChannel(c);
        ctx.setShowEditChannel(true);
      },
      invite: (c) => {
        const ctx = channelActionsCtxRef.current;
        if (!ctx.selectedWorkspace) return;
        ctx.setInviteContext({
          type: "channel",
          workspaceId: ctx.selectedWorkspace,
          channelId: c.id,
          channelName: c.name,
          workspaceName: ctx.currentWorkspace?.name || "",
          workspaceLogoUrl: ctx.currentWorkspace?.logo_url,
        });
      },
      toggleMute: (c) => {
        const ctx = channelActionsCtxRef.current;
        const membership = ctx.myMembers.find((m) => m.channel_id === c.id);
        if (membership) {
          ctx.zero.mutate(
            mutators.channel_members.update({
              id: membership.id,
              muted: !membership.muted,
            }),
          );
        }
      },
      toggleArchive: (c) => channelActionsCtxRef.current.handleToggleArchive(c),
      leave: (c) => channelActionsCtxRef.current.handleLeaveChannel(c),
      remove: (c) => {
        const ctx = channelActionsCtxRef.current;
        ctx.setContextChannel(c);
        ctx.setShowDeleteChannel(true);
      },
      notifyChange: (c, notify) => {
        const ctx = channelActionsCtxRef.current;
        const membership = ctx.myMembers.find((m) => m.channel_id === c.id);
        if (membership) {
          ctx.zero.mutate(
            mutators.channel_members.update({
              id: membership.id,
              notify,
            }),
          );
        }
      },
      huddle: (c) => {
        const ctx = channelActionsCtxRef.current;
        // Audio-by-default huddle, matching Slack's UX. Workspace is
        // optional at the route level, so DMs from Home view work
        // even when selectedWorkspace is null.
        void ctx.joinCall({
          channelId: c.id,
          workspaceId: ctx.selectedWorkspace,
          channelName: c.name,
          mode: "audio",
        });
      },
    }),
    [],
  );

  return {
    channelActions,
    handleLeaveWorkspace,
    handleDeleteChannel,
    handleLeaveChannel,
    handleToggleArchive,
  };
}
